import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import {
  getAllOrders,
  getOrderById,
  updateOrderStatus,
  confirmShipping,
  cancelOrder,
  createInvoiceFromOrder,
  Order,
} from '../services/orders';
import { getImageUrl } from '../services/api';
import {
  exportInvoice,
  downloadInvoice,
  sendInvoice,
  getInvoiceByOrderId,
  InvoiceWithExport,
} from '../services/invoices';
import './AdminOrdersPage.css';

const AdminOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});
  const [invoice, setInvoice] = useState<InvoiceWithExport | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filter changes
  }, [statusFilter]);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, currentPage]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: 20,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await getAllOrders(params);
      // Sicherstellen, dass data ein Array ist
      const ordersData = Array.isArray(response.data) ? response.data : [];
      setOrders(ordersData);
      setPagination(response.pagination);

      // Clientseitige Filterung für Suche (kann später serverseitig gemacht werden)
      let filtered = ordersData;
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase().trim();
        filtered = filtered.filter(order => {
          const orderNumberMatch = order.orderNumber?.toLowerCase().includes(query);
          const internalOrderNumberMatch = order.internalOrderNumber?.toLowerCase().includes(query);
          return orderNumberMatch || internalOrderNumberMatch;
        });
      }
      setFilteredOrders(Array.isArray(filtered) ? filtered : []);
    } catch (error) {
      console.error('Error loading orders:', error);
      // Bei Fehler: leere Arrays setzen
      setOrders([]);
      setFilteredOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clientseitige Filterung für Suche
    // Sicherstellen, dass orders ein Array ist
    const ordersArray = Array.isArray(orders) ? orders : [];
    
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      const filtered = ordersArray.filter(order => {
        const orderNumberMatch = order.orderNumber?.toLowerCase().includes(query);
        const internalOrderNumberMatch = order.internalOrderNumber?.toLowerCase().includes(query);
        return orderNumberMatch || internalOrderNumberMatch;
      });
      setFilteredOrders(Array.isArray(filtered) ? filtered : []);
    } else {
      setFilteredOrders(ordersArray);
    }
  }, [searchQuery, orders]);

  const handleOrderClick = async (orderId: string) => {
    try {
      const order = await getOrderById(orderId);
      setSelectedOrder(order);
      // Lade Bild asynchron - nur wenn ein Bild vorhanden ist
      if (order.id && !imageUrls[order.id] && order.prescriptionImageMimeType) {
        try {
          const url = await getImageUrl(order.id);
          if (url) {
            setImageUrls(prev => ({ ...prev, [order.id]: url }));
          }
        } catch (error) {
          // Only log if it's not a 404 (expected when no image)
          if (error instanceof Error && !error.message.includes('404')) {
            console.error('Error loading image:', error);
          }
        }
      }
      // Lade Invoice für diese Order
      await loadInvoiceForOrder(orderId);
    } catch (error) {
      console.error('Error loading order details:', error);
    }
  };

  const loadInvoiceForOrder = async (orderId: string) => {
    setInvoiceLoading(true);
    try {
      const invoiceData = await getInvoiceByOrderId(orderId);
      setInvoice(invoiceData);
    } catch (error: any) {
      // 404 is expected when no invoice exists yet - don't log as error
      if (error.response?.status !== 404) {
        console.error('Error loading invoice:', error);
      }
      setInvoice(null);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleStatusUpdate = async (
    orderId: string,
    newStatus: 'PENDING' | 'PARTS_ORDERED' | 'SHIPPED' | 'COMPLETED'
  ) => {
    try {
      const updated = await updateOrderStatus(orderId, newStatus);
      await loadOrders();
      if (selectedOrder?.id === orderId) {
        const refreshed = await getOrderById(orderId);
        setSelectedOrder(refreshed);
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      const errorMessage =
        error.response?.data?.error || error.message || 'Fehler beim Aktualisieren des Status';
      alert(`Fehler beim Aktualisieren des Status: ${errorMessage}`);
    }
  };

  const handleShippingConfirmation = async (orderId: string) => {
    if (
      !window.confirm(
        'Möchten Sie wirklich den Versand bestätigen? Eine E-Mail wird an den Händler gesendet und der Status wird auf "Versendet" gesetzt.'
      )
    ) {
      return;
    }
    try {
      await confirmShipping(orderId);
      alert('Versandbestätigung erfolgreich gesendet und Status auf "Versendet" gesetzt!');
      await loadOrders();
      if (selectedOrder?.id === orderId) {
        const refreshed = await getOrderById(orderId);
        setSelectedOrder(refreshed);
      }
    } catch (error: any) {
      console.error('Error confirming shipping:', error);
      const errorMessage =
        error.response?.data?.error || 'Fehler beim Senden der Versandbestätigung';
      alert(errorMessage);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    if (!selectedOrder) return;

    if (selectedOrder.status === 'CANCELLED') {
      alert('Diese Bestellung wurde bereits storniert');
      return;
    }

    if (
      !window.confirm(
        'Möchten Sie diese Bestellung wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden.'
      )
    ) {
      return;
    }

    try {
      await cancelOrder(orderId, true);
      alert('Bestellung erfolgreich storniert');
      await loadOrders();
      if (selectedOrder?.id === orderId) {
        const refreshed = await getOrderById(orderId);
        setSelectedOrder(refreshed);
      }
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      const errorMessage = error.response?.data?.error || 'Fehler beim Stornieren der Bestellung';
      alert(errorMessage);
    }
  };

  const handleCreateInvoice = async (orderId: string) => {
    if (!selectedOrder) return;

    // Prüfe ob Bestellung im Status COMPLETED ist
    if (selectedOrder.status !== 'COMPLETED') {
      alert(
        'Rechnungen können nur für Bestellungen im Status "erledigt" erstellt werden.'
      );
      return;
    }

    // Prüfe ob Bestellung einen Preis hat
    if (!selectedOrder.finalPrice || selectedOrder.finalPrice <= 0) {
      alert(
        'Diese Bestellung hat keinen gültigen Preis. Eine Rechnung kann nicht erstellt werden.'
      );
      return;
    }

    if (!window.confirm('Möchten Sie für diese erledigte Bestellung eine Rechnung erstellen?')) {
      return;
    }

    try {
      const result = await createInvoiceFromOrder(orderId);
      alert(`Rechnung erfolgreich erstellt!\nRechnungsnummer: ${result.invoice.invoiceNumber}`);
      await loadOrders();
      if (selectedOrder?.id === orderId) {
        const refreshed = await getOrderById(orderId);
        setSelectedOrder(refreshed);
        // Lade Invoice neu
        await loadInvoiceForOrder(orderId);
      }
    } catch (error: any) {
      console.error('Error creating invoice:', error);
      const errorMessage = error.response?.data?.error || 'Fehler beim Erstellen der Rechnung';
      alert(errorMessage);
    }
  };

  const handleExportInvoice = async (invoiceId: string) => {
    try {
      const result = await exportInvoice(invoiceId);

      if (!result.export.validated) {
        // validationErrors ist ein JSON-Objekt, das errors und warnings enthalten kann
        const validationData: any = result.export.validationErrors || {};
        const errors = Array.isArray(validationData) ? validationData : validationData.errors || [];
        const warnings = validationData.warnings || [];

        let message = 'Die Rechnung wurde exportiert, aber die Validierung ist fehlgeschlagen.\n\n';

        if (errors.length > 0) {
          message += `Validierungsfehler:\n${errors.map((e: any) => `- ${typeof e === 'string' ? e : JSON.stringify(e)}`).join('\n')}\n\n`;
        }

        if (warnings.length > 0) {
          message += `Warnungen:\n${warnings.map((w: any) => `- ${typeof w === 'string' ? w : JSON.stringify(w)}`).join('\n')}\n\n`;
        }

        message += 'Bitte korrigieren Sie die Fehler, bevor Sie die Rechnung versenden.';

        alert(message);
        return;
      }

      // Prüfe auf Warnungen in validationErrors
      const validationData: any = result.export.validationErrors || {};
      const warnings = validationData.warnings || [];

      if (warnings.length > 0) {
        const warningsText = warnings
          .map((w: any) => `- ${typeof w === 'string' ? w : JSON.stringify(w)}`)
          .join('\n');
        alert(
          `Rechnung erfolgreich exportiert und validiert!\n\nWarnungen:\n${warningsText}\n\nSie können die Rechnung jetzt herunterladen oder versenden.`
        );
      } else {
        alert(
          'Rechnung erfolgreich exportiert und validiert! Sie können die Rechnung jetzt herunterladen oder versenden.'
        );
      }

      // Lade Invoice neu, um Export-Status zu aktualisieren
      if (selectedOrder) {
        await loadInvoiceForOrder(selectedOrder.id);
      }
    } catch (error: any) {
      console.error('Error exporting invoice:', error);
      let errorMessage = 'Fehler beim Exportieren der Rechnung';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(`Fehler beim Exportieren:\n${errorMessage}`);
    }
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      await downloadInvoice(invoiceId);
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      let errorMessage = 'Fehler beim Herunterladen der Rechnung';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (error.response?.status === 404) {
        errorMessage =
          'Rechnung wurde noch nicht exportiert. Bitte exportieren Sie die Rechnung zuerst.';
      }

      alert(`Fehler beim Herunterladen:\n${errorMessage}`);
    }
  };

  const handleSendInvoice = async (invoiceId: string) => {
    if (!invoice) return;

    // Prüfe ob Export existiert und validiert ist
    if (!invoice.exports || invoice.exports.length === 0) {
      alert('Die Rechnung wurde noch nicht exportiert. Bitte exportieren Sie die Rechnung zuerst.');
      return;
    }

    const latestExport = invoice.exports[0];

    if (!latestExport.validated) {
      const errors = latestExport.validationErrors || {};
      const errorList = Array.isArray(errors) ? errors : errors.errors || [];

      let message =
        'Die Rechnung wurde noch nicht validiert oder die Validierung ist fehlgeschlagen.\n\n';

      if (errorList.length > 0) {
        message += `Validierungsfehler:\n${errorList.map((e: any) => `- ${typeof e === 'string' ? e : JSON.stringify(e)}`).join('\n')}\n\n`;
      }

      message +=
        'Bitte korrigieren Sie die Fehler und exportieren Sie die Rechnung erneut, bevor Sie sie versenden.';

      alert(message);
      return;
    }

    if (
      !window.confirm(
        `Möchten Sie die Rechnung ${invoice.invoiceNumber} wirklich per E-Mail an den Händler versenden?`
      )
    ) {
      return;
    }

    try {
      const result = await sendInvoice(invoiceId);
      alert(result.message);

      // Lade Invoice neu, um Status zu aktualisieren
      if (selectedOrder) {
        await loadInvoiceForOrder(selectedOrder.id);
      }
    } catch (error: any) {
      console.error('Error sending invoice:', error);
      let errorMessage = 'Fehler beim Versenden der Rechnung';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(`Fehler beim Versenden:\n${errorMessage}`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  return (
    <div>
      <AdminHeader />
      <div className="container">
        <div className="filter-section">
          <label>
            Status filtern:
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Alle</option>
              <option value="PENDING">Ausstehend</option>
              <option value="PARTS_ORDERED">Teile bestellt</option>
              <option value="SHIPPED">Versendet</option>
              <option value="COMPLETED">Erledigt</option>
              <option value="CANCELLED">Storniert</option>
            </select>
          </label>
        </div>

        {loading ? (
          <div className="loading">Lade Bestellungen...</div>
        ) : (
          <>
            <div className="search-container" style={{ marginBottom: '20px' }}>
              <input
                type="text"
                placeholder="Suche nach Bestellnummer oder interner Auftragsnummer..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '16px',
                }}
              />
            </div>
            {/* Desktop Table View */}
            <div className="orders-table-container orders-table-desktop">
              <table className="table">
                <thead>
                  <tr>
                    <th>Interne Auftragsnummer</th>
                    <th>Bestellnummer</th>
                    <th>Händler</th>
                    <th>Modell</th>
                    <th>Produkttyp</th>
                    <th>Lieferung</th>
                    <th>Preis</th>
                    <th>Status</th>
                    <th>Datum</th>
                    <th>Details</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {(!Array.isArray(filteredOrders) || filteredOrders.length === 0) ? (
                    <tr>
                      <td colSpan={11} style={{ textAlign: 'center' }}>
                        {searchQuery || statusFilter !== 'all'
                          ? 'Keine Bestellungen gefunden'
                          : 'Keine Bestellungen vorhanden'}
                      </td>
                    </tr>
                  ) : (
                    (Array.isArray(filteredOrders) ? filteredOrders : []).map(order => (
                      <tr key={order.id}>
                        <td>
                          {order.internalOrderNumber || (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                          )}
                        </td>
                        <td>{order.orderNumber}</td>
                        <td>{order.dealer?.username || 'Unbekannt'}</td>
                        <td>{order.goggleModel?.name || 'Unbekannt'}</td>
                        <td>{order.productType === 'CLIP' ? 'Clip' : 'Komplette Brille'}</td>
                        <td>{order.deliveryType === 'SHOP' ? 'Shop' : 'Kunde'}</td>
                        <td>
                          {order.finalPrice !== undefined ? (
                            <div>
                              <strong>{order.finalPrice.toFixed(2)} €</strong>
                              {(order.discountPercent ?? 0) > 0 && (
                                <div style={{ fontSize: '0.85em', color: '#666' }}>
                                  ({order.basePrice?.toFixed(2)} € - {order.discountPercent}%)
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#999' }}>N/A</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              order.status === 'CANCELLED'
                                ? 'cancelled'
                                : order.status === 'COMPLETED'
                                  ? 'completed'
                                  : order.status === 'SHIPPED'
                                    ? 'shipped'
                                    : order.status === 'PARTS_ORDERED'
                                      ? 'parts-ordered'
                                      : 'pending'
                            }`}
                          >
                            {order.status === 'CANCELLED'
                              ? 'Storniert'
                              : order.status === 'COMPLETED'
                                ? 'Erledigt'
                                : order.status === 'SHIPPED'
                                  ? 'Versendet'
                                  : order.status === 'PARTS_ORDERED'
                                    ? 'Teile bestellt'
                                    : 'Ausstehend'}
                          </span>
                        </td>
                        <td>{formatDate(order.createdAt)}</td>
                        <td>
                          <button
                            onClick={() => handleOrderClick(order.id)}
                            className="btn btn-primary"
                          >
                            Details
                          </button>
                        </td>
                        <td>
                          {order.status === 'PENDING' && (
                            <button
                              onClick={() => handleStatusUpdate(order.id, 'PARTS_ORDERED')}
                              className="btn btn-secondary"
                              style={{ marginRight: '5px', marginBottom: '5px' }}
                            >
                              Teile bestellt
                            </button>
                          )}
                          {order.status === 'PARTS_ORDERED' && (
                            <button
                              onClick={() => handleShippingConfirmation(order.id)}
                              className="btn btn-primary"
                              style={{ marginRight: '5px', marginBottom: '5px' }}
                            >
                              Versand bestätigen
                            </button>
                          )}
                          {order.status === 'SHIPPED' && (
                            <button
                              onClick={() => handleStatusUpdate(order.id, 'COMPLETED')}
                              className="btn btn-success"
                              style={{ marginRight: '5px', marginBottom: '5px' }}
                            >
                              Als erledigt markieren
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="orders-cards-container orders-cards-mobile">
              {(!Array.isArray(filteredOrders) || filteredOrders.length === 0) ? (
                <div className="order-card-empty">
                  {searchQuery || statusFilter !== 'all'
                    ? 'Keine Bestellungen gefunden'
                    : 'Keine Bestellungen vorhanden'}
                </div>
              ) : (
                (Array.isArray(filteredOrders) ? filteredOrders : []).map(order => (
                  <div key={order.id} className="order-card">
                    <div className="order-card-header">
                      <div className="order-card-header-main">
                        <div className="order-card-number">
                          <strong>{order.orderNumber}</strong>
                        </div>
                        <span
                          className={`status-badge ${
                            order.status === 'CANCELLED'
                              ? 'cancelled'
                              : order.status === 'COMPLETED'
                                ? 'completed'
                                : order.status === 'SHIPPED'
                                  ? 'shipped'
                                  : order.status === 'PARTS_ORDERED'
                                    ? 'parts-ordered'
                                    : 'pending'
                          }`}
                        >
                          {order.status === 'CANCELLED'
                            ? 'Storniert'
                            : order.status === 'COMPLETED'
                              ? 'Erledigt'
                              : order.status === 'SHIPPED'
                                ? 'Versendet'
                                : order.status === 'PARTS_ORDERED'
                                  ? 'Teile bestellt'
                                  : 'Ausstehend'}
                        </span>
                      </div>
                      <button
                        onClick={() => handleOrderClick(order.id)}
                        className="btn btn-primary order-card-details-btn"
                      >
                        Details
                      </button>
                    </div>
                    <div className="order-card-body">
                      {order.internalOrderNumber && (
                        <div className="order-card-row">
                          <span className="order-card-label">Interne Auftragsnummer:</span>
                          <span className="order-card-value">{order.internalOrderNumber}</span>
                        </div>
                      )}
                      <div className="order-card-row">
                        <span className="order-card-label">Händler:</span>
                        <span className="order-card-value">
                          {order.dealer?.username || 'Unbekannt'}
                        </span>
                      </div>
                      <div className="order-card-row">
                        <span className="order-card-label">Modell:</span>
                        <span className="order-card-value">
                          {order.goggleModel?.name || 'Unbekannt'}
                        </span>
                      </div>
                      <div className="order-card-row">
                        <span className="order-card-label">Produkttyp:</span>
                        <span className="order-card-value">
                          {order.productType === 'CLIP' ? 'Clip' : 'Komplette Brille'}
                        </span>
                      </div>
                      <div className="order-card-row">
                        <span className="order-card-label">Lieferung:</span>
                        <span className="order-card-value">
                          {order.deliveryType === 'SHOP' ? 'Shop' : 'Kunde'}
                        </span>
                      </div>
                      {order.finalPrice !== undefined && (
                        <div className="order-card-row">
                          <span className="order-card-label">Preis:</span>
                          <span className="order-card-value">
                            <strong>{order.finalPrice.toFixed(2)} €</strong>
                            {(order.discountPercent ?? 0) > 0 && (
                              <span
                                style={{ fontSize: '0.85em', color: '#666', marginLeft: '5px' }}
                              >
                                ({order.basePrice?.toFixed(2)} € - {order.discountPercent}%)
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                      <div className="order-card-row">
                        <span className="order-card-label">Datum:</span>
                        <span className="order-card-value">{formatDate(order.createdAt)}</span>
                      </div>
                      <div className="order-card-actions">
                        {order.status === 'PENDING' && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, 'PARTS_ORDERED')}
                            className="btn btn-secondary"
                            style={{ width: '100%', marginBottom: '5px' }}
                          >
                            Teile bestellt
                          </button>
                        )}
                        {order.status === 'PARTS_ORDERED' && (
                          <button
                            onClick={() => handleShippingConfirmation(order.id)}
                            className="btn btn-primary"
                            style={{ width: '100%', marginBottom: '5px' }}
                          >
                            Versand bestätigen
                          </button>
                        )}
                        {order.status === 'SHIPPED' && (
                          <button
                            onClick={() => handleStatusUpdate(order.id, 'COMPLETED')}
                            className="btn btn-success"
                            style={{ width: '100%', marginBottom: '5px' }}
                          >
                            Als erledigt markieren
                          </button>
                        )}
                        {order.status === 'COMPLETED' && (
                          <button
                            onClick={() => handleCreateInvoice(order.id)}
                            className="btn btn-primary"
                            disabled={invoiceLoading}
                            style={{ width: '100%', marginBottom: '5px' }}
                          >
                            Rechnung erstellen
                          </button>
                        )}
                        {order.status !== 'CANCELLED' && order.status !== 'PENDING' && (
                          <button
                            onClick={() => handleCancelOrder(order.id)}
                            className="btn btn-danger"
                            style={{ width: '100%' }}
                          >
                            Stornieren
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination Controls */}
            {pagination && pagination.totalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className="btn btn-secondary"
                    style={{
                      opacity: !pagination.hasPrev ? 0.5 : 1,
                      cursor: !pagination.hasPrev ? 'not-allowed' : 'pointer',
                    }}
                  >
                    ‹ Zurück
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ margin: '0 10px' }}>
                    Seite {pagination.page} von {pagination.totalPages}
                  </span>
                  <span style={{ color: '#666', fontSize: '0.9em' }}>
                    ({pagination.total} Einträge insgesamt)
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className="btn btn-secondary"
                    style={{
                      opacity: !pagination.hasNext ? 0.5 : 1,
                      cursor: !pagination.hasNext ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Weiter ›
                  </button>
                </div>
              </div>
            )}

            {selectedOrder && (
              <div
                className="modal"
                onClick={() => {
                  setSelectedOrder(null);
                  setInvoice(null); // Reset invoice when closing modal
                }}
              >
                <div className="modal-content" onClick={e => e.stopPropagation()}>
                  <button
                    className="modal-close"
                    onClick={() => {
                      setSelectedOrder(null);
                      setInvoice(null); // Reset invoice when closing modal
                    }}
                  >
                    ×
                  </button>
                  <h2>Bestelldetails</h2>
                  <div className="order-details">
                    <p>
                      <strong>Bestellnummer:</strong> {selectedOrder.orderNumber}
                    </p>
                    {selectedOrder.internalOrderNumber && (
                      <p>
                        <strong>Interne Auftragsnummer:</strong> {selectedOrder.internalOrderNumber}
                      </p>
                    )}
                    <p>
                      <strong>Händler:</strong> {selectedOrder.dealer?.username}
                    </p>
                    <p>
                      <strong>E-Mail:</strong> {selectedOrder.dealer?.email}
                    </p>
                    <p>
                      <strong>Modell:</strong> {selectedOrder.goggleModel?.name}
                    </p>
                    <p>
                      <strong>Beschreibung:</strong>{' '}
                      {selectedOrder.goggleModel?.description || 'Keine'}
                    </p>
                    <p>
                      <strong>Produkttyp:</strong>{' '}
                      {selectedOrder.productType === 'CLIP' ? 'Clip' : 'Komplette Brille'}
                    </p>
                    {selectedOrder.glassType && (
                      <p>
                        <strong>Glas:</strong> {selectedOrder.glassType}
                      </p>
                    )}
                    {selectedOrder.bandType && (
                      <p>
                        <strong>Band:</strong> {selectedOrder.bandType}
                      </p>
                    )}
                    {selectedOrder.finalPrice !== undefined && (
                      <div
                        style={{
                          marginTop: '1rem',
                          padding: '1rem',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                        }}
                      >
                        <p>
                          <strong>Preisübersicht:</strong>
                        </p>
                        <p>Basispreis: {selectedOrder.basePrice?.toFixed(2) || '0.00'} €</p>
                        {(selectedOrder.discountPercent ?? 0) > 0 && (
                          <p>Rabatt: {selectedOrder.discountPercent}%</p>
                        )}
                        <p>
                          <strong>Endpreis: {selectedOrder.finalPrice.toFixed(2)} €</strong>
                        </p>
                      </div>
                    )}
                    <p>
                      <strong>Lieferung:</strong>{' '}
                      {selectedOrder.deliveryType === 'SHOP'
                        ? 'An den Shop'
                        : 'Direkt an den Kunden'}
                    </p>
                    {selectedOrder.customerAddress && (
                      <p>
                        <strong>Kundenadresse:</strong> {selectedOrder.customerAddress}
                      </p>
                    )}
                    {(() => {
                      const hasPrescriptionValues =
                        selectedOrder.leftEyeSph !== undefined ||
                        selectedOrder.leftEyeCyl !== undefined ||
                        selectedOrder.leftEyeA !== undefined ||
                        selectedOrder.rightEyeSph !== undefined ||
                        selectedOrder.rightEyeCyl !== undefined ||
                        selectedOrder.rightEyeA !== undefined;

                      const fmt = (v?: number | null) =>
                        v === null || v === undefined ? '—' : v.toFixed(2).replace('.', ',');
                      const fmtA = (v?: number | null) =>
                        v === null || v === undefined ? '—' : `${v}°`;

                      if (!hasPrescriptionValues) return null;

                      return (
                        <div
                          style={{
                            marginTop: '1rem',
                            padding: '1rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                          }}
                        >
                          <h4 style={{ margin: '0 0 0.5rem 0' }}>Brillenpass-Werte</h4>
                          <div
                            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}
                          >
                            <div>
                              <strong>Linkes Auge</strong>
                              <div style={{ marginTop: '0.35rem', fontSize: '0.95em' }}>
                                <div>Sph: {fmt(selectedOrder.leftEyeSph)}</div>
                                <div>Cyl: {fmt(selectedOrder.leftEyeCyl)}</div>
                                <div>A: {fmtA(selectedOrder.leftEyeA)}</div>
                              </div>
                            </div>
                            <div>
                              <strong>Rechtes Auge</strong>
                              <div style={{ marginTop: '0.35rem', fontSize: '0.95em' }}>
                                <div>Sph: {fmt(selectedOrder.rightEyeSph)}</div>
                                <div>Cyl: {fmt(selectedOrder.rightEyeCyl)}</div>
                                <div>A: {fmtA(selectedOrder.rightEyeA)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    <p>
                      <strong>Status:</strong>{' '}
                      {selectedOrder.status === 'CANCELLED'
                        ? 'Storniert'
                        : selectedOrder.status === 'COMPLETED'
                          ? 'Erledigt'
                          : selectedOrder.status === 'SHIPPED'
                            ? 'Versendet'
                            : selectedOrder.status === 'PARTS_ORDERED'
                              ? 'Teile bestellt'
                              : 'Ausstehend'}
                    </p>
                    {selectedOrder.notes && (
                      <div>
                        <p>
                          <strong>Notizen:</strong>
                        </p>
                        <div className="notes-content">{selectedOrder.notes}</div>
                      </div>
                    )}
                    <p>
                      <strong>Erstellt am:</strong> {formatDate(selectedOrder.createdAt)}
                    </p>
                    <p>
                      <strong>Rezeptbild:</strong>
                    </p>
                    <div className="prescription-image">
                      {imageUrls[selectedOrder.id] ? (
                        <img
                          src={imageUrls[selectedOrder.id]}
                          alt="Rezept"
                          style={{
                            maxWidth: '100%',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                          }}
                          onError={e => {
                            console.error('Error loading image:', e);
                            (e.target as HTMLImageElement).style.display = 'none';
                            const parent = (e.target as HTMLImageElement).parentElement;
                            if (parent) {
                              parent.innerHTML =
                                '<p style="color: red;">Fehler beim Laden des Bildes. Bitte versuche es später erneut.</p>';
                            }
                          }}
                        />
                      ) : (
                        <p>Lade Bild...</p>
                      )}
                    </div>
                    <div
                      style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}
                    >
                      {selectedOrder.status === 'PENDING' && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder.id, 'PARTS_ORDERED');
                            setSelectedOrder(null);
                          }}
                          className="btn btn-secondary"
                        >
                          Teile bestellt
                        </button>
                      )}
                      {selectedOrder.status === 'PARTS_ORDERED' && (
                        <button
                          onClick={() => {
                            handleShippingConfirmation(selectedOrder.id);
                          }}
                          className="btn btn-primary"
                        >
                          Versand bestätigen
                        </button>
                      )}
                      {selectedOrder.status === 'SHIPPED' && (
                        <button
                          onClick={() => {
                            handleStatusUpdate(selectedOrder.id, 'COMPLETED');
                            setSelectedOrder(null);
                          }}
                          className="btn btn-success"
                        >
                          Als erledigt markieren
                        </button>
                      )}
                      {selectedOrder.status === 'COMPLETED' &&
                        selectedOrder.finalPrice != null &&
                        selectedOrder.finalPrice > 0 &&
                        !invoice &&
                        !invoiceLoading && (
                          <button
                            onClick={() => {
                              handleCreateInvoice(selectedOrder.id);
                            }}
                            className="btn btn-primary"
                            style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                          >
                            Rechnung erstellen
                          </button>
                        )}
                      {invoiceLoading && (
                        <div
                          style={{
                            marginTop: '20px',
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #dee2e6',
                          }}
                        >
                          <p>Lade Rechnungsinformationen...</p>
                        </div>
                      )}
                      {invoice && !invoiceLoading && (
                        <div
                          style={{
                            marginTop: '20px',
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid #dee2e6',
                          }}
                        >
                          <h3 style={{ marginTop: 0, marginBottom: '10px' }}>
                            Rechnung {invoice.invoiceNumber}
                          </h3>
                          <p style={{ margin: '5px 0' }}>
                            <strong>Status:</strong>{' '}
                            {invoice.status === 'DRAFT'
                              ? 'Entwurf'
                              : invoice.status === 'ISSUED'
                                ? 'Ausgestellt'
                                : invoice.status === 'SENT'
                                  ? 'Versendet'
                                  : invoice.status === 'PAID'
                                    ? 'Bezahlt'
                                    : invoice.status === 'CANCELLED'
                                      ? 'Storniert'
                                      : invoice.status}
                          </p>
                          <p style={{ margin: '5px 0' }}>
                            <strong>Betrag:</strong> {invoice.totalsGross.toFixed(2)} €
                          </p>
                          {invoice.exports && invoice.exports.length > 0 ? (
                            <div style={{ marginTop: '10px' }}>
                              {invoice.exports[0].validated ? (
                                <p style={{ color: '#28a745', margin: '5px 0' }}>✓ Validiert</p>
                              ) : (
                                <p style={{ color: '#dc3545', margin: '5px 0' }}>
                                  ✗ Nicht validiert
                                </p>
                              )}
                            </div>
                          ) : (
                            <p style={{ color: '#666', margin: '5px 0', fontSize: '0.9em' }}>
                              Noch nicht exportiert
                            </p>
                          )}
                          <div
                            style={{
                              marginTop: '15px',
                              display: 'flex',
                              gap: '10px',
                              flexWrap: 'wrap',
                            }}
                          >
                            {(!invoice.exports || invoice.exports.length === 0) && (
                              <button
                                onClick={() => handleExportInvoice(invoice.id)}
                                className="btn btn-primary"
                                style={{ backgroundColor: 'var(--accent)', borderColor: 'var(--accent)' }}
                              >
                                Rechnung exportieren
                              </button>
                            )}
                            {invoice.exports && invoice.exports.length > 0 && (
                              <>
                                {!invoice.exports[0].validated && (
                                  <button
                                    onClick={() => handleExportInvoice(invoice.id)}
                                    className="btn btn-warning"
                                    style={{
                                      backgroundColor: '#ffc107',
                                      borderColor: '#ffc107',
                                      color: '#212529',
                                    }}
                                  >
                                    Erneut exportieren
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDownloadInvoice(invoice.id)}
                                  className="btn btn-info"
                                  style={{ backgroundColor: 'var(--info)', borderColor: 'var(--info)' }}
                                  disabled={!invoice.exports[0].validated}
                                >
                                  Rechnung herunterladen
                                </button>
                                <button
                                  onClick={() => handleSendInvoice(invoice.id)}
                                  className="btn btn-success"
                                  style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                                  disabled={!invoice.exports[0].validated}
                                >
                                  Per E-Mail versenden
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          handleCancelOrder(selectedOrder.id);
                        }}
                        className="btn btn-danger"
                        disabled={selectedOrder.status === 'CANCELLED'}
                        style={{
                          opacity: selectedOrder.status === 'CANCELLED' ? 0.5 : 1,
                          cursor: selectedOrder.status === 'CANCELLED' ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {selectedOrder.status === 'CANCELLED'
                          ? 'Bereits storniert'
                          : 'Bestellung stornieren'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminOrdersPage;
